'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';

export interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
}

export const LANGUAGES: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
];

type TranslationKey =
  // ── Auth ──────────────────────────────────────────────────────────────────
  | 'welcomeBack' | 'signIn' | 'createAccount' | 'signInToWorkspace' |'forgotPassword'| 'emailAddress' | 'password' | 'rememberMe' |'signInBtn' | 'orContinueWith'
  // ── Topbar ────────────────────────────────────────────────────────────────
  | 'role' | 'location' | 'search' | 'notifications' | 'settings' | 'logout'
  | 'aiBotActive'| 'fhirConnected' | 'markAllRead' | 'allCaughtUp' |'noNewNotifications' | 'darkMode' | 'lightMode'
  // ── Sidebar groups ────────────────────────────────────────────────────────
  | 'groupClinical' | 'groupRevenue' | 'groupPharmacy' | 'groupAI' |'groupCommunication' | 'groupAnalytics' | 'groupCompliance'
  // ── Sidebar nav items ─────────────────────────────────────────────────────
  | 'navClinicalDashboard' | 'navPatients' | 'navPatientPortal' |'navProviders'| 'navScheduling' | 'navTelemedicine' |'navBillingClaims'| 'navPharmacyPOS' | 'navAIBots' |'navTeamMessaging' | 'navPatientCommunication' | 'navNotifications'
  | 'navReports'| 'navAnalyticsDashboard' | 'navWorkflowAutomation' |'navAuditLog' | 'navPatientDataExport' | 'navSettings' | 'navHelpSupport' |'navCredentialVerification' | 'navGLConsolidated'
  | 'navInventoryManagement' | 'navAccounting' | 'navReconciliation' | 'navVendorPurchase'
  // ── Page titles ───────────────────────────────────────────────────────────
  | 'pageClinicalAnalytics' | 'pageClinicalAnalyticsSub' |'pagePatientManagement'| 'pagePatientManagementSub' |'pagePatientPortal'| 'pagePatientPortalSub' |'pageProviderManagement'| 'pageProviderManagementSub' |'pageScheduling'| 'pageSchedulingSub' |'pageTelemedicine'| 'pageTelemedicineSub' |'pageBillingClaims'| 'pageBillingClaimsSub' |'pagePharmacyPOS'| 'pagePharmacyPOSSub' |'pageAIBots'| 'pageAIBotsSub' |'pageTeamMessaging'| 'pageTeamMessagingSub' |'pagePatientCommunication'| 'pagePatientCommunicationSub' |'pageNotifications' | 'pageNotificationsSub'
  | 'pageReports'| 'pageReportsSub' |'pageAnalyticsDashboard'| 'pageAnalyticsDashboardSub' |'pageWorkflowAutomation'| 'pageWorkflowAutomationSub' |'pageAuditLog'| 'pageAuditLogSub' |'pagePatientDataExport'| 'pagePatientDataExportSub' |'pageSettings' | 'pageSettingsSub'
  // ── Common UI ─────────────────────────────────────────────────────────────
  | 'save' | 'cancel' | 'delete' | 'edit' | 'add' | 'view' | 'export'
  | 'filter'| 'searchPlaceholder' | 'loading' | 'noData' | 'refresh' |'status'| 'actions' | 'date' | 'time' | 'name' | 'email' | 'phone' |'active' | 'inactive' | 'pending' | 'completed' | 'cancelled'
  | 'all'| 'today' | 'thisWeek' | 'thisMonth' | 'custom' |'patients' | 'billing' | 'pharmacy' | 'reports' | 'dashboard'
  // ── Legacy ────────────────────────────────────────────────────────────────
  | 'aiPoweredCare' | 'aiPoweredCareSub' | 'hipaaCompliant';

type Translations = Record<TranslationKey, string>;

const translations: Record<string, Translations> = {
  en: {
    // Auth
    welcomeBack: 'Welcome back',
    signIn: 'Sign In',
    createAccount: 'Create Account',
    signInToWorkspace: 'Sign in to your NobiMED AI workspace',
    forgotPassword: 'Forgot password?',
    emailAddress: 'Email Address',
    password: 'Password',
    rememberMe: 'Keep me signed in for 30 days',
    signInBtn: 'Sign In to NobiMED AI',
    orContinueWith: 'or continue with email',
    // Topbar
    role: 'Role',
    location: 'Location',
    search: 'Search...',
    notifications: 'Notifications',
    settings: 'Settings',
    logout: 'Logout',
    aiBotActive: 'AI Bot Active',
    fhirConnected: 'FHIR Connected',
    markAllRead: 'Mark all read',
    allCaughtUp: 'All caught up!',
    noNewNotifications: 'No new notifications',
    darkMode: 'Switch to Dark Mode',
    lightMode: 'Switch to Light Mode',
    // Sidebar groups
    groupClinical: 'CLINICAL',
    groupRevenue: 'REVENUE',
    groupPharmacy: 'PHARMACY',
    groupAI: 'AI',
    groupCommunication: 'COMMUNICATION',
    groupAnalytics: 'ANALYTICS',
    groupCompliance: 'COMPLIANCE',
    // Sidebar nav
    navClinicalDashboard: 'Clinical Dashboard',
    navPatients: 'Patients',
    navPatientPortal: 'Patient Portal',
    navProviders: 'Providers',
    navScheduling: 'Scheduling',
    navTelemedicine: 'Telemedicine',
    navBillingClaims: 'Billing & Claims',
    navPharmacyPOS: 'Pharmacy POS',
    navAIBots: 'AI Bots',
    navTeamMessaging: 'Team Messaging',
    navPatientCommunication: 'Patient Communication',
    navNotifications: 'Notifications',
    navReports: 'Reports & BI',
    navAnalyticsDashboard: 'Analytics Dashboard',
    navWorkflowAutomation: 'Workflow Automation',
    navAuditLog: 'Audit Log',
    navPatientDataExport: 'Patient Data Export',
    navSettings: 'Settings',
    navHelpSupport: 'Help & Support',
    navCredentialVerification: 'Credential Verification',
    navGLConsolidated: 'GL Consolidated View',
    navVendorPurchase: 'Vendor Purchase',
    navInventoryManagement: 'Inventory Management',
    navAccounting: 'Accounting',
    navReconciliation: 'Reconciliation',
    // Page titles
    pageClinicalAnalytics: 'Clinical Analytics',
    pageClinicalAnalyticsSub: 'All Locations · Live',
    pagePatientManagement: 'Patient Management',
    pagePatientManagementSub: 'Active patient registry · 3 locations · FHIR synced',
    pagePatientPortal: 'Patient Portal',
    pagePatientPortalSub: 'Self-service patient access · Secure messaging · Records',
    pageProviderManagement: 'Provider Management',
    pageProviderManagementSub: 'Credentialing · Schedules · Performance · All providers',
    pageScheduling: 'Appointment Scheduling',
    pageSchedulingSub: 'Calendar · Availability · Reminders · All locations',
    pageTelemedicine: 'Telemedicine',
    pageTelemedicineSub: 'Virtual visits · Video calls · Remote care',
    pageBillingClaims: 'Billing & Claims',
    pageBillingClaimsSub: 'Revenue cycle management · AI-assisted coding & claims · All locations',
    pagePharmacyPOS: 'Pharmacy POS',
    pagePharmacyPOSSub: 'Dispensing · Inventory · Drug interactions · All locations',
    pageAIBots: 'AI Bots',
    pageAIBotsSub: 'Intelligent automation · Front desk · Clinical notes · Billing',
    pageTeamMessaging: 'Team Messaging',
    pageTeamMessagingSub: 'Secure clinical communication · HIPAA compliant',
    pagePatientCommunication: 'Patient Communication',
    pagePatientCommunicationSub: 'SMS · Email · Portal messaging · Broadcast',
    pageNotifications: 'Notifications',
    pageNotificationsSub: 'Real-time alerts · Clinical · Billing · System',
    pageReports: 'Reports & Business Intelligence',
    pageReportsSub: 'Analytics · Exports · Compliance reports · All locations',
    pageAnalyticsDashboard: 'Real-Time Analytics',
    pageAnalyticsDashboardSub: 'Live KPIs · Revenue · Patient flow · Provider performance',
    pageWorkflowAutomation: 'Workflow Automation',
    pageWorkflowAutomationSub: 'Automated workflows · Triggers · Actions · AI-powered',
    pageAuditLog: 'Audit Log & Compliance',
    pageAuditLogSub: 'PHI access log · HIPAA compliance · Security events',
    pagePatientDataExport: 'Patient Data Export',
    pagePatientDataExportSub: 'FHIR R4 · CSV · JSON · Bulk export · HIPAA compliant',
    pageSettings: 'Settings',
    pageSettingsSub: 'System configuration · Integrations · User preferences',
    // Common UI
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    view: 'View',
    export: 'Export',
    filter: 'Filter',
    searchPlaceholder: 'Search...',
    loading: 'Loading...',
    noData: 'No data available',
    refresh: 'Refresh',
    status: 'Status',
    actions: 'Actions',
    date: 'Date',
    time: 'Time',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    active: 'Active',
    inactive: 'Inactive',
    pending: 'Pending',
    completed: 'Completed',
    cancelled: 'Cancelled',
    all: 'All',
    today: 'Today',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    custom: 'Custom',
    patients: 'Patients',
    billing: 'Billing',
    pharmacy: 'Pharmacy',
    reports: 'Reports',
    dashboard: 'Dashboard',
    // Legacy
    aiPoweredCare: 'AI-Powered Care.',
    aiPoweredCareSub: 'En Todas Partes.',
    hipaaCompliant: 'Cumple HIPAA · SOC 2 Tipo II · FHIR R4',
  },

  es: {
    welcomeBack: 'Bienvenido de nuevo',
    signIn: 'Iniciar Sesión',
    createAccount: 'Crear Cuenta',
    signInToWorkspace: 'Inicia sesión en tu espacio NobiMED AI',
    forgotPassword: '¿Olvidaste tu contraseña?',
    emailAddress: 'Correo Electrónico',
    password: 'Contraseña',
    rememberMe: 'Mantenerme conectado por 30 días',
    signInBtn: 'Iniciar Sesión en NobiMED AI',
    orContinueWith: 'o continúa con correo',
    role: 'Rol',
    location: 'Ubicación',
    search: 'Buscar...',
    notifications: 'Notificaciones',
    settings: 'Configuración',
    logout: 'Cerrar Sesión',
    aiBotActive: 'Bot IA Activo',
    fhirConnected: 'FHIR Conectado',
    markAllRead: 'Marcar todo como leído',
    allCaughtUp: '¡Todo al día!',
    noNewNotifications: 'Sin nuevas notificaciones',
    darkMode: 'Cambiar a Modo Oscuro',
    lightMode: 'Cambiar a Modo Claro',
    groupClinical: 'CLÍNICO',
    groupRevenue: 'INGRESOS',
    groupPharmacy: 'FARMACIA',
    groupAI: 'IA',
    groupCommunication: 'COMUNICACIÓN',
    groupAnalytics: 'ANALÍTICA',
    groupCompliance: 'CUMPLIMIENTO',
    navClinicalDashboard: 'Panel Clínico',
    navPatients: 'Pacientes',
    navPatientPortal: 'Portal del Paciente',
    navProviders: 'Proveedores',
    navScheduling: 'Agenda',
    navTelemedicine: 'Telemedicina',
    navBillingClaims: 'Facturación',
    navPharmacyPOS: 'Farmacia POS',
    navAIBots: 'Bots IA',
    navTeamMessaging: 'Mensajería de Equipo',
    navPatientCommunication: 'Comunicación con Pacientes',
    navNotifications: 'Notificaciones',
    navReports: 'Informes y BI',
    navAnalyticsDashboard: 'Panel de Analítica',
    navWorkflowAutomation: 'Automatización',
    navAuditLog: 'Registro de Auditoría',
    navPatientDataExport: 'Exportar Datos',
    navSettings: 'Configuración',
    navHelpSupport: 'Ayuda y Soporte',
    navCredentialVerification: 'Verificación de Credenciales',
    navGLConsolidated: 'Vista GL Consolidada',
    navVendorPurchase: 'Compra de Proveedores',
    navInventoryManagement: 'Gestión de Inventario',
    navAccounting: 'Contabilidad',
    navReconciliation: 'Conciliación',
    pageClinicalAnalytics: 'Analítica Clínica',
    pageClinicalAnalyticsSub: 'Todas las Ubicaciones · En Vivo',
    pagePatientManagement: 'Gestión de Pacientes',
    pagePatientManagementSub: 'Registro activo · 3 ubicaciones · Sincronizado con FHIR',
    pagePatientPortal: 'Portal del Paciente',
    pagePatientPortalSub: 'Acceso de autoservicio · Mensajería segura · Registros',
    pageProviderManagement: 'Gestión de Proveedores',
    pageProviderManagementSub: 'Credenciales · Horarios · Rendimiento · Todos los proveedores',
    pageScheduling: 'Programación de Citas',
    pageSchedulingSub: 'Calendario · Disponibilidad · Recordatorios · Todas las ubicaciones',
    pageTelemedicine: 'Telemedicina',
    pageTelemedicineSub: 'Visitas virtuales · Videollamadas · Atención remota',
    pageBillingClaims: 'Facturación y Reclamaciones',
    pageBillingClaimsSub: 'Ciclo de ingresos · Codificación asistida por IA · Todas las ubicaciones',
    pagePharmacyPOS: 'Farmacia POS',
    pagePharmacyPOSSub: 'Dispensación · Inventario · Interacciones · Todas las ubicaciones',
    pageAIBots: 'Bots de IA',
    pageAIBotsSub: 'Automatización inteligente · Recepción · Notas clínicas · Facturación',
    pageTeamMessaging: 'Mensajería de Equipo',
    pageTeamMessagingSub: 'Comunicación clínica segura · Cumple HIPAA',
    pagePatientCommunication: 'Comunicación con Pacientes',
    pagePatientCommunicationSub: 'SMS · Email · Mensajería portal · Difusión',
    pageNotifications: 'Notificaciones',
    pageNotificationsSub: 'Alertas en tiempo real · Clínico · Facturación · Sistema',
    pageReports: 'Informes e Inteligencia de Negocio',
    pageReportsSub: 'Analítica · Exportaciones · Informes de cumplimiento · Todas las ubicaciones',
    pageAnalyticsDashboard: 'Analítica en Tiempo Real',
    pageAnalyticsDashboardSub: 'KPIs en vivo · Ingresos · Flujo de pacientes · Rendimiento',
    pageWorkflowAutomation: 'Automatización de Flujos',
    pageWorkflowAutomationSub: 'Flujos automatizados · Disparadores · Acciones · IA',
    pageAuditLog: 'Registro de Auditoría y Cumplimiento',
    pageAuditLogSub: 'Registro de acceso PHI · Cumplimiento HIPAA · Eventos de seguridad',
    pagePatientDataExport: 'Exportación de Datos de Pacientes',
    pagePatientDataExportSub: 'FHIR R4 · CSV · JSON · Exportación masiva · Cumple HIPAA',
    pageSettings: 'Configuración',
    pageSettingsSub: 'Configuración del sistema · Integraciones · Preferencias',
    save: 'Guardar',
    cancel: 'Cancelar',
    delete: 'Eliminar',
    edit: 'Editar',
    add: 'Agregar',
    view: 'Ver',
    export: 'Exportar',
    filter: 'Filtrar',
    searchPlaceholder: 'Buscar...',
    loading: 'Cargando...',
    noData: 'Sin datos disponibles',
    refresh: 'Actualizar',
    status: 'Estado',
    actions: 'Acciones',
    date: 'Fecha',
    time: 'Hora',
    name: 'Nombre',
    email: 'Correo',
    phone: 'Teléfono',
    active: 'Activo',
    inactive: 'Inactivo',
    pending: 'Pendiente',
    completed: 'Completado',
    cancelled: 'Cancelado',
    all: 'Todos',
    today: 'Hoy',
    thisWeek: 'Esta Semana',
    thisMonth: 'Este Mes',
    custom: 'Personalizado',
    patients: 'Pacientes',
    billing: 'Facturación',
    pharmacy: 'Farmacia',
    reports: 'Informes',
    dashboard: 'Panel',
    aiPoweredCare: 'Atención con IA.',
    aiPoweredCareSub: 'En Todas Partes.',
    hipaaCompliant: 'Cumple HIPAA · SOC 2 Tipo II · FHIR R4',
  },

  fr: {
    welcomeBack: 'Bon retour',
    signIn: 'Se Connecter',
    createAccount: 'Créer un Compte',
    signInToWorkspace: 'Connectez-vous à votre espace NobiMED AI',
    forgotPassword: 'Mot de passe oublié?',
    emailAddress: 'Adresse Email',
    password: 'Mot de Passe',
    rememberMe: 'Rester connecté pendant 30 jours',
    signInBtn: 'Se Connecter à NobiMED AI',
    orContinueWith: 'ou continuer avec email',
    role: 'Rôle',
    location: 'Emplacement',
    search: 'Rechercher...',
    notifications: 'Notifications',
    settings: 'Paramètres',
    logout: 'Déconnexion',
    aiBotActive: 'Bot IA Actif',
    fhirConnected: 'FHIR Connecté',
    markAllRead: 'Tout marquer comme lu',
    allCaughtUp: 'Tout est à jour!',
    noNewNotifications: 'Aucune nouvelle notification',
    darkMode: 'Passer en Mode Sombre',
    lightMode: 'Passer en Mode Clair',
    groupClinical: 'CLINIQUE',
    groupRevenue: 'REVENUS',
    groupPharmacy: 'PHARMACIE',
    groupAI: 'IA',
    groupCommunication: 'COMMUNICATION',
    groupAnalytics: 'ANALYTIQUE',
    groupCompliance: 'CONFORMITÉ',
    navClinicalDashboard: 'Tableau de Bord Clinique',
    navPatients: 'Patients',
    navPatientPortal: 'Portail Patient',
    navProviders: 'Prestataires',
    navScheduling: 'Planification',
    navTelemedicine: 'Télémédecine',
    navBillingClaims: 'Facturation',
    navPharmacyPOS: 'Pharmacie POS',
    navAIBots: 'Bots IA',
    navTeamMessaging: 'Messagerie Équipe',
    navPatientCommunication: 'Communication Patient',
    navNotifications: 'Notifications',
    navReports: 'Rapports & BI',
    navAnalyticsDashboard: 'Tableau Analytique',
    navWorkflowAutomation: 'Automatisation',
    navAuditLog: 'Journal d\'Audit',
    navPatientDataExport: 'Export de Données',
    navSettings: 'Paramètres',
    navHelpSupport: 'Aide & Support',
    navCredentialVerification: 'Vérification des Credenciales',
    navGLConsolidated: 'Vue GL Consolidée',
    navVendorPurchase: 'Achat Fournisseur',
    navInventoryManagement: 'Gestion des Stocks',
    navAccounting: 'Comptabilité',
    navReconciliation: 'Réconciliation',
    pageClinicalAnalytics: 'Analytique Clinique',
    pageClinicalAnalyticsSub: 'Tous les Sites · En Direct',
    pagePatientManagement: 'Gestion des Patients',
    pagePatientManagementSub: 'Registre actif · 3 sites · Synchronisé FHIR',
    pagePatientPortal: 'Portail Patient',
    pagePatientPortalSub: 'Accès libre-service · Messagerie sécurisée · Dossiers',
    pageProviderManagement: 'Gestion des Prestataires',
    pageProviderManagementSub: 'Accréditations · Horaires · Performance · Tous les prestataires',
    pageScheduling: 'Planification des Rendez-vous',
    pageSchedulingSub: 'Calendrier · Disponibilité · Rappels · Tous les sites',
    pageTelemedicine: 'Télémédecine',
    pageTelemedicineSub: 'Visites virtuelles · Appels vidéo · Soins à distance',
    pageBillingClaims: 'Facturation et Réclamations',
    pageBillingClaimsSub: 'Cycle de revenus · Codage assisté par IA · Tous les sites',
    pagePharmacyPOS: 'Pharmacie POS',
    pagePharmacyPOSSub: 'Dispensation · Inventaire · Interactions · Tous les sites',
    pageAIBots: 'Bots IA',
    pageAIBotsSub: 'Automatisation intelligente · Accueil · Notes cliniques · Facturation',
    pageTeamMessaging: 'Messagerie d\'Équipe',
    pageTeamMessagingSub: 'Communication clinique sécurisée · Conforme HIPAA',
    pagePatientCommunication: 'Communication Patient',
    pagePatientCommunicationSub: 'SMS · Email · Messagerie portail · Diffusion',
    pageNotifications: 'Notifications',
    pageNotificationsSub: 'Alertes en temps réel · Clinique · Facturation · Système',
    pageReports: 'Rapports et Intelligence d\'Affaires',
    pageReportsSub: 'Analytique · Exports · Rapports de conformité · Tous les sites',
    pageAnalyticsDashboard: 'Analytique en Temps Réel',
    pageAnalyticsDashboardSub: 'KPIs en direct · Revenus · Flux patients · Performance',
    pageWorkflowAutomation: 'Automatisation des Flux',
    pageWorkflowAutomationSub: 'Flux automatisés · Déclencheurs · Actions · IA',
    pageAuditLog: 'Journal d\'Audit et Conformité',
    pageAuditLogSub: 'Journal d\'accès PHI · Conformité HIPAA · Événements de sécurité',
    pagePatientDataExport: 'Export des Données Patient',
    pagePatientDataExportSub: 'FHIR R4 · CSV · JSON · Export en masse · Conforme HIPAA',
    pageSettings: 'Paramètres',
    pageSettingsSub: 'Configuration système · Intégrations · Préférences',
    save: 'Enregistrer',
    cancel: 'Annuler',
    delete: 'Supprimer',
    edit: 'Modifier',
    add: 'Ajouter',
    view: 'Voir',
    export: 'Exporter',
    filter: 'Filtrer',
    searchPlaceholder: 'Rechercher...',
    loading: 'Chargement...',
    noData: 'Aucune donnée disponible',
    refresh: 'Actualiser',
    status: 'Statut',
    actions: 'Actions',
    date: 'Date',
    time: 'Heure',
    name: 'Nom',
    email: 'Email',
    phone: 'Téléphone',
    active: 'Actif',
    inactive: 'Inactif',
    pending: 'En attente',
    completed: 'Terminé',
    cancelled: 'Annulé',
    all: 'Tous',
    today: 'Aujourd\'hui',
    thisWeek: 'Cette Semaine',
    thisMonth: 'Ce Mois',
    custom: 'Personnalisé',
    patients: 'Patients',
    billing: 'Facturation',
    pharmacy: 'Pharmacie',
    reports: 'Rapports',
    dashboard: 'Tableau de Bord',
    aiPoweredCare: 'Soins Alimentés par IA.',
    aiPoweredCareSub: 'Partout Où Vous Pratiquez.',
    hipaaCompliant: 'Conforme HIPAA · SOC 2 Type II · FHIR R4',
  },

  pt: {
    welcomeBack: 'Bem-vindo de volta',
    signIn: 'Entrar',
    createAccount: 'Criar Conta',
    signInToWorkspace: 'Entre no seu espaço NobiMED AI',
    forgotPassword: 'Esqueceu a senha?',
    emailAddress: 'Endereço de Email',
    password: 'Senha',
    rememberMe: 'Manter conectado por 30 dias',
    signInBtn: 'Entrar no NobiMED AI',
    orContinueWith: 'ou continuar com email',
    role: 'Função',
    location: 'Localização',
    search: 'Pesquisar...',
    notifications: 'Notificações',
    settings: 'Configurações',
    logout: 'Sair',
    aiBotActive: 'Bot IA Ativo',
    fhirConnected: 'FHIR Conectado',
    markAllRead: 'Marcar tudo como lido',
    allCaughtUp: 'Tudo em dia!',
    noNewNotifications: 'Sem novas notificações',
    darkMode: 'Mudar para Modo Escuro',
    lightMode: 'Mudar para Modo Claro',
    groupClinical: 'CLÍNICO',
    groupRevenue: 'RECEITA',
    groupPharmacy: 'FARMÁCIA',
    groupAI: 'IA',
    groupCommunication: 'COMUNICAÇÃO',
    groupAnalytics: 'ANÁLISE',
    groupCompliance: 'CONFORMIDADE',
    navClinicalDashboard: 'Painel Clínico',
    navPatients: 'Pacientes',
    navPatientPortal: 'Portal do Paciente',
    navProviders: 'Provedores',
    navScheduling: 'Agendamento',
    navTelemedicine: 'Telemedicina',
    navBillingClaims: 'Faturamento',
    navPharmacyPOS: 'Farmácia POS',
    navAIBots: 'Bots de IA',
    navTeamMessaging: 'Mensagens da Equipe',
    navPatientCommunication: 'Comunicação com Pacientes',
    navNotifications: 'Notificações',
    navReports: 'Relatórios & BI',
    navAnalyticsDashboard: 'Painel Analítico',
    navWorkflowAutomation: 'Automação',
    navAuditLog: 'Registro de Auditoria',
    navPatientDataExport: 'Exportar Dados',
    navSettings: 'Configurações',
    navHelpSupport: 'Ajuda & Suporte',
    navCredentialVerification: 'Verificação de Credenciais',
    navGLConsolidated: 'Visão GL Consolidada',
    navVendorPurchase: 'Compra de Fornecedores',
    navInventoryManagement: 'Gestão de Inventário',
    navAccounting: 'Contabilidade',
    navReconciliation: 'Reconciliação',
    pageClinicalAnalytics: 'Analítica Clínica',
    pageClinicalAnalyticsSub: 'Todas as Localizações · Ao Vivo',
    pagePatientManagement: 'Gestão de Pacientes',
    pagePatientManagementSub: 'Registro ativo · 3 localizações · Sincronizado FHIR',
    pagePatientPortal: 'Portal do Paciente',
    pagePatientPortalSub: 'Acesso self-service · Mensagens seguras · Registros',
    pageProviderManagement: 'Gestão de Provedores',
    pageProviderManagementSub: 'Credenciais · Horários · Desempenho · Todos os provedores',
    pageScheduling: 'Agendamento de Consultas',
    pageSchedulingSub: 'Calendário · Disponibilidade · Lembretes · Todas as localizações',
    pageTelemedicine: 'Telemedicina',
    pageTelemedicineSub: 'Visitas virtuais · Videochamadas · Cuidados remotos',
    pageBillingClaims: 'Faturamento e Sinistros',
    pageBillingClaimsSub: 'Ciclo de receita · Codificação assistida por IA · Todas as localizações',
    pagePharmacyPOS: 'Farmácia POS',
    pagePharmacyPOSSub: 'Dispensação · Inventário · Interações · Todas as localizações',
    pageAIBots: 'Bots de IA',
    pageAIBotsSub: 'Automação inteligente · Recepção · Notas clínicas · Faturamento',
    pageTeamMessaging: 'Mensagens da Equipe',
    pageTeamMessagingSub: 'Comunicação clínica segura · Conforme HIPAA',
    pagePatientCommunication: 'Comunicação com Pacientes',
    pagePatientCommunicationSub: 'SMS · Email · Mensagens portal · Transmissão',
    pageNotifications: 'Notificações',
    pageNotificationsSub: 'Alertas em tempo real · Clínico · Faturamento · Sistema',
    pageReports: 'Relatórios e Inteligência de Negócios',
    pageReportsSub: 'Analítica · Exportações · Relatórios de conformidade · Todas as localizações',
    pageAnalyticsDashboard: 'Analítica em Tempo Real',
    pageAnalyticsDashboardSub: 'KPIs ao vivo · Receita · Fluxo de pacientes · Desempenho',
    pageWorkflowAutomation: 'Automação de Fluxos',
    pageWorkflowAutomationSub: 'Fluxos automatizados · Gatilhos · Ações · IA',
    pageAuditLog: 'Registro de Auditoria e Conformidade',
    pageAuditLogSub: 'Registro de acesso PHI · Conformidade HIPAA · Eventos de segurança',
    pagePatientDataExport: 'Exportação de Dados de Pacientes',
    pagePatientDataExportSub: 'FHIR R4 · CSV · JSON · Exportação em massa · Conforme HIPAA',
    pageSettings: 'Configurações',
    pageSettingsSub: 'Configuração do sistema · Integrações · Preferências',
    save: 'Salvar',
    cancel: 'Cancelar',
    delete: 'Excluir',
    edit: 'Editar',
    add: 'Adicionar',
    view: 'Ver',
    export: 'Exportar',
    filter: 'Filtrar',
    searchPlaceholder: 'Pesquisar...',
    loading: 'Carregando...',
    noData: 'Sem dados disponíveis',
    refresh: 'Atualizar',
    status: 'Status',
    actions: 'Ações',
    date: 'Data',
    time: 'Hora',
    name: 'Nome',
    email: 'Email',
    phone: 'Telefone',
    active: 'Ativo',
    inactive: 'Inativo',
    pending: 'Pendente',
    completed: 'Concluído',
    cancelled: 'Cancelado',
    all: 'Todos',
    today: 'Hoje',
    thisWeek: 'Esta Semana',
    thisMonth: 'Este Mês',
    custom: 'Personalizado',
    patients: 'Pacientes',
    billing: 'Faturamento',
    pharmacy: 'Farmácia',
    reports: 'Relatórios',
    dashboard: 'Painel',
    aiPoweredCare: 'Cuidados com IA.',
    aiPoweredCareSub: 'Em Qualquer Lugar.',
    hipaaCompliant: 'Conforme HIPAA · SOC 2 Tipo II · FHIR R4',
  },

  de: {
    welcomeBack: 'Willkommen zurück',
    signIn: 'Anmelden',
    createAccount: 'Konto Erstellen',
    signInToWorkspace: 'Melden Sie sich bei Ihrem NobiMED AI Arbeitsbereich an',
    forgotPassword: 'Passwort vergessen?',
    emailAddress: 'E-Mail-Adresse',
    password: 'Passwort',
    rememberMe: '30 Tage angemeldet bleiben',
    signInBtn: 'Bei NobiMED AI Anmelden',
    orContinueWith: 'oder mit E-Mail fortfahren',
    role: 'Rolle',
    location: 'Standort',
    search: 'Suchen...',
    notifications: 'Benachrichtigungen',
    settings: 'Einstellungen',
    logout: 'Abmelden',
    aiBotActive: 'KI-Bot Aktiv',
    fhirConnected: 'FHIR Verbunden',
    markAllRead: 'Alle als gelesen markieren',
    allCaughtUp: 'Alles erledigt!',
    noNewNotifications: 'Keine neuen Benachrichtigungen',
    darkMode: 'Dunkelmodus aktivieren',
    lightMode: 'Hellmodus aktivieren',
    groupClinical: 'KLINISCH',
    groupRevenue: 'EINNAHMEN',
    groupPharmacy: 'APOTHEKE',
    groupAI: 'KI',
    groupCommunication: 'KOMMUNIKATION',
    groupAnalytics: 'ANALYTIK',
    groupCompliance: 'COMPLIANCE',
    navClinicalDashboard: 'Klinisches Dashboard',
    navPatients: 'Patienten',
    navPatientPortal: 'Patientenportal',
    navProviders: 'Anbieter',
    navScheduling: 'Terminplanung',
    navTelemedicine: 'Telemedizin',
    navBillingClaims: 'Abrechnung',
    navPharmacyPOS: 'Apotheke POS',
    navAIBots: 'KI-Bots',
    navTeamMessaging: 'Team-Nachrichten',
    navPatientCommunication: 'Patientenkommunikation',
    navNotifications: 'Benachrichtigungen',
    navReports: 'Berichte & BI',
    navAnalyticsDashboard: 'Analytik-Dashboard',
    navWorkflowAutomation: 'Workflow-Automatisierung',
    navAuditLog: 'Prüfprotokoll',
    navPatientDataExport: 'Datenexport',
    navSettings: 'Einstellungen',
    navHelpSupport: 'Hilfe & Support',
    navCredentialVerification: 'Verifikation von Anmeldeinformationen',
    navGLConsolidated: 'Konsolidierte GL-Ansicht',
    navVendorPurchase: 'Lieferantenkauf',
    navInventoryManagement: 'Bestandsverwaltung',
    navAccounting: 'Buchhaltung',
    navReconciliation: 'Abstimmung',
    pageClinicalAnalytics: 'Klinische Analytik',
    pageClinicalAnalyticsSub: 'Alle Standorte · Live',
    pagePatientManagement: 'Patientenverwaltung',
    pagePatientManagementSub: 'Aktives Register · 3 Standorte · FHIR synchronisiert',
    pagePatientPortal: 'Patientenportal',
    pagePatientPortalSub: 'Selbstbedienungszugang · Sichere Nachrichten · Akten',
    pageProviderManagement: 'Anbieterverwaltung',
    pageProviderManagementSub: 'Zulassungen · Zeitpläne · Leistung · Alle Anbieter',
    pageScheduling: 'Terminplanung',
    pageSchedulingSub: 'Kalender · Verfügbarkeit · Erinnerungen · Alle Standorte',
    pageTelemedicine: 'Telemedizin',
    pageTelemedicineSub: 'Virtuelle Besuche · Videoanrufe · Fernversorgung',
    pageBillingClaims: 'Abrechnung und Ansprüche',
    pageBillingClaimsSub: 'Umsatzzyklus · KI-gestützte Kodierung · Alle Standorte',
    pagePharmacyPOS: 'Apotheke POS',
    pagePharmacyPOSSub: 'Ausgabe · Inventar · Wechselwirkungen · Alle Standorte',
    pageAIBots: 'KI-Bots',
    pageAIBotsSub: 'Intelligente Automatisierung · Empfang · Klinische Notizen · Abrechnung',
    pageTeamMessaging: 'Team-Nachrichten',
    pageTeamMessagingSub: 'Sichere klinische Kommunikation · HIPAA-konform',
    pagePatientCommunication: 'Patientenkommunikation',
    pagePatientCommunicationSub: 'SMS · E-Mail · Portal-Nachrichten · Broadcast',
    pageNotifications: 'Benachrichtigungen',
    pageNotificationsSub: 'Echtzeit-Warnungen · Klinisch · Abrechnung · System',
    pageReports: 'Berichte und Business Intelligence',
    pageReportsSub: 'Analytik · Exporte · Compliance-Berichte · Alle Standorte',
    pageAnalyticsDashboard: 'Echtzeit-Analytik',
    pageAnalyticsDashboardSub: 'Live-KPIs · Einnahmen · Patientenfluss · Leistung',
    pageWorkflowAutomation: 'Workflow-Automatisierung',
    pageWorkflowAutomationSub: 'Automatisierte Workflows · Auslöser · Aktionen · KI',
    pageAuditLog: 'Prüfprotokoll und Compliance',
    pageAuditLogSub: 'PHI-Zugriffsprotokoll · HIPAA-Compliance · Sicherheitsereignisse',
    pagePatientDataExport: 'Patientendatenexport',
    pagePatientDataExportSub: 'FHIR R4 · CSV · JSON · Massenexport · HIPAA-konform',
    pageSettings: 'Einstellungen',
    pageSettingsSub: 'Systemkonfiguration · Integrationen · Benutzereinstellungen',
    save: 'Speichern',
    cancel: 'Abbrechen',
    delete: 'Löschen',
    edit: 'Bearbeiten',
    add: 'Hinzufügen',
    view: 'Ansehen',
    export: 'Exportieren',
    filter: 'Filtern',
    searchPlaceholder: 'Suchen...',
    loading: 'Laden...',
    noData: 'Keine Daten verfügbar',
    refresh: 'Aktualisieren',
    status: 'Status',
    actions: 'Aktionen',
    date: 'Datum',
    time: 'Zeit',
    name: 'Name',
    email: 'E-Mail',
    phone: 'Telefon',
    active: 'Aktiv',
    inactive: 'Inaktiv',
    pending: 'Ausstehend',
    completed: 'Abgeschlossen',
    cancelled: 'Abgebrochen',
    all: 'Alle',
    today: 'Heute',
    thisWeek: 'Diese Woche',
    thisMonth: 'Diesen Monat',
    custom: 'Benutzerdefiniert',
    patients: 'Patienten',
    billing: 'Abrechnung',
    pharmacy: 'Apotheke',
    reports: 'Berichte',
    dashboard: 'Dashboard',
    aiPoweredCare: 'KI-gestützte Pflege.',
    aiPoweredCareSub: 'Überall Wo Sie Praktizieren.',
    hipaaCompliant: 'HIPAA-konform · SOC 2 Typ II · FHIR R4',
  },

  ar: {
    welcomeBack: 'مرحباً بعودتك',
    signIn: 'تسجيل الدخول',
    createAccount: 'إنشاء حساب',
    signInToWorkspace: 'سجّل دخولك إلى مساحة NobiMED AI',
    forgotPassword: 'نسيت كلمة المرور؟',
    emailAddress: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    rememberMe: 'ابقَ متصلاً لمدة 30 يوماً',
    signInBtn: 'تسجيل الدخول إلى NobiMED AI',
    orContinueWith: 'أو المتابعة بالبريد الإلكتروني',
    role: 'الدور',
    location: 'الموقع',
    search: 'بحث...',
    notifications: 'الإشعارات',
    settings: 'الإعدادات',
    logout: 'تسجيل الخروج',
    aiBotActive: 'روبوت الذكاء الاصطناعي نشط',
    fhirConnected: 'FHIR متصل',
    markAllRead: 'تحديد الكل كمقروء',
    allCaughtUp: 'كل شيء على ما يرام!',
    noNewNotifications: 'لا توجد إشعارات جديدة',
    darkMode: 'التبديل إلى الوضع الداكن',
    lightMode: 'التبديل إلى الوضع الفاتح',
    groupClinical: 'السريري',
    groupRevenue: 'الإيرادات',
    groupPharmacy: 'الصيدلية',
    groupAI: 'الذكاء الاصطناعي',
    groupCommunication: 'التواصل',
    groupAnalytics: 'التحليلات',
    groupCompliance: 'الامتثال',
    navClinicalDashboard: 'لوحة التحكم السريرية',
    navPatients: 'المرضى',
    navPatientPortal: 'بوابة المريض',
    navProviders: 'مقدمو الخدمة',
    navScheduling: 'الجدولة',
    navTelemedicine: 'الطب عن بُعد',
    navBillingClaims: 'الفواتير والمطالبات',
    navPharmacyPOS: 'نقطة بيع الصيدلية',
    navAIBots: 'روبوتات الذكاء الاصطناعي',
    navTeamMessaging: 'مراسلة الفريق',
    navPatientCommunication: 'التواصل مع المرضى',
    navNotifications: 'الإشعارات',
    navReports: 'التقارير وذكاء الأعمال',
    navAnalyticsDashboard: 'لوحة التحليلات',
    navWorkflowAutomation: 'أتمتة سير العمل',
    navAuditLog: 'سجل التدقيق',
    navPatientDataExport: 'تصدير بيانات المرضى',
    navSettings: 'الإعدادات',
    navHelpSupport: 'المساعدة والدعم',
    navCredentialVerification: 'التحقق من الهوية',
    navGLConsolidated: 'عرض GL الموحد',
    navVendorPurchase: 'شراء المورد',
    navInventoryManagement: 'إدارة المخزون',
    navAccounting: 'المحاسبة',
    navReconciliation: 'التسوية',
    pageClinicalAnalytics: 'التحليلات السريرية',
    pageClinicalAnalyticsSub: 'جميع المواقع · مباشر',
    pagePatientManagement: 'إدارة المرضى',
    pagePatientManagementSub: 'السجل النشط · 3 مواقع · متزامن مع FHIR',
    pagePatientPortal: 'بوابة المريض',
    pagePatientPortalSub: 'وصول ذاتي · رسائل آمنة · السجلات',
    pageProviderManagement: 'إدارة مقدمي الخدمة',
    pageProviderManagementSub: 'الاعتماد · الجدول · الأداء · جميع المزودين',
    pageScheduling: 'جدولة المواعيد',
    pageSchedulingSub: 'التقويم · التوفر · التذكيرات · جميع المواقع',
    pageTelemedicine: 'الطب عن بُعد',
    pageTelemedicineSub: 'زيارات افتراضية · مكالمات فيديو · رعاية عن بُعد',
    pageBillingClaims: 'الفواتير والمطالبات',
    pageBillingClaimsSub: 'دورة الإيرادات · ترميز بمساعدة الذكاء الاصطناعي · جميع المواقع',
    pagePharmacyPOS: 'نقطة بيع الصيدلية',
    pagePharmacyPOSSub: 'الصرف · المخزون · التفاعلات · جميع المواقع',
    pageAIBots: 'روبوتات الذكاء الاصطناعي',
    pageAIBotsSub: 'أتمتة ذكية · الاستقبال · الملاحظات السريرية · الفواتير',
    pageTeamMessaging: 'مراسلة الفريق',
    pageTeamMessagingSub: 'تواصل سريري آمن · متوافق مع HIPAA',
    pagePatientCommunication: 'التواصل مع المرضى',
    pagePatientCommunicationSub: 'رسائل نصية · بريد إلكتروني · رسائل البوابة · بث',
    pageNotifications: 'الإشعارات',
    pageNotificationsSub: 'تنبيهات فورية · سريري · فواتير · نظام',
    pageReports: 'التقارير وذكاء الأعمال',
    pageReportsSub: 'التحليلات · التصديرات · تقارير الامتثال · جميع المواقع',
    pageAnalyticsDashboard: 'التحليلات في الوقت الفعلي',
    pageAnalyticsDashboardSub: 'مؤشرات الأداء الرئيسية المباشرة · الإيرادات · تدفق المرضى · الأداء',
    pageWorkflowAutomation: 'أتمتة سير العمل',
    pageWorkflowAutomationSub: 'سير عمل آلي · محفزات · إجراءات · ذكاء اصطناعي',
    pageAuditLog: 'سجل التدقيق والامتثال',
    pageAuditLogSub: 'سجل الوصول إلى PHI · امتثال HIPAA · أحداث الأمان',
    pagePatientDataExport: 'تصدير بيانات المرضى',
    pagePatientDataExportSub: 'FHIR R4 · CSV · JSON · تصدير مجمع · متوافق مع HIPAA',
    pageSettings: 'الإعدادات',
    pageSettingsSub: 'تكوين النظام · التكاملات · تفضيلات المستخدم',
    save: 'حفظ',
    cancel: 'إلغاء',
    delete: 'حذف',
    edit: 'تعديل',
    add: 'إضافة',
    view: 'عرض',
    export: 'تصدير',
    filter: 'تصفية',
    searchPlaceholder: 'بحث...',
    loading: 'جارٍ التحميل...',
    noData: 'لا توجد بيانات',
    refresh: 'تحديث',
    status: 'الحالة',
    actions: 'الإجراءات',
    date: 'التاريخ',
    time: 'الوقت',
    name: 'الاسم',
    email: 'البريد الإلكتروني',
    phone: 'الهاتف',
    active: 'نشط',
    inactive: 'غير نشط',
    pending: 'معلق',
    completed: 'مكتمل',
    cancelled: 'ملغى',
    all: 'الكل',
    today: 'اليوم',
    thisWeek: 'هذا الأسبوع',
    thisMonth: 'هذا الشهر',
    custom: 'مخصص',
    patients: 'المرضى',
    billing: 'الفواتير',
    pharmacy: 'الصيدلية',
    reports: 'التقارير',
    dashboard: 'لوحة التحكم',
    aiPoweredCare: 'رعاية مدعومة بالذكاء الاصطناعي.',
    aiPoweredCareSub: 'في كل مكان تمارس فيه.',
    hipaaCompliant: 'متوافق مع HIPAA · SOC 2 نوع II · FHIR R4',
  },

  zh: {
    welcomeBack: '欢迎回来',
    signIn: '登录',
    createAccount: '创建账户',
    signInToWorkspace: '登录您的 NobiMED AI 工作区',
    forgotPassword: '忘记密码？',
    emailAddress: '电子邮件地址',
    password: '密码',
    rememberMe: '保持登录30天',
    signInBtn: '登录 NobiMED AI',
    orContinueWith: '或使用邮箱继续',
    role: '角色',
    location: '位置',
    search: '搜索...',
    notifications: '通知',
    settings: '设置',
    logout: '退出登录',
    aiBotActive: 'AI机器人活跃',
    fhirConnected: 'FHIR已连接',
    markAllRead: '全部标为已读',
    allCaughtUp: '全部处理完毕！',
    noNewNotifications: '没有新通知',
    darkMode: '切换到深色模式',
    lightMode: '切换到浅色模式',
    groupClinical: '临床',
    groupRevenue: '收入',
    groupPharmacy: '药房',
    groupAI: '人工智能',
    groupCommunication: '通讯',
    groupAnalytics: '分析',
    groupCompliance: '合规',
    navClinicalDashboard: '临床仪表板',
    navPatients: '患者',
    navPatientPortal: '患者门户',
    navProviders: '医疗提供者',
    navScheduling: '预约排班',
    navTelemedicine: '远程医疗',
    navBillingClaims: '账单与索赔',
    navPharmacyPOS: '药房POS',
    navAIBots: 'AI机器人',
    navTeamMessaging: '团队消息',
    navPatientCommunication: '患者沟通',
    navNotifications: '通知',
    navReports: '报告与BI',
    navAnalyticsDashboard: '分析仪表板',
    navWorkflowAutomation: '工作流自动化',
    navAuditLog: '审计日志',
    navPatientDataExport: '患者数据导出',
    navSettings: '设置',
    navHelpSupport: '帮助与支持',
    navCredentialVerification: '凭据验证',
    navGLConsolidated: 'GL合并视图',
    navVendorPurchase: '供应商采购',
    navInventoryManagement: '库存管理',
    navAccounting: '会计',
    navReconciliation: '对账',
    pageClinicalAnalytics: '临床分析',
    pageClinicalAnalyticsSub: '所有地点 · 实时',
    pagePatientManagement: '患者管理',
    pagePatientManagementSub: '活跃患者注册 · 3个地点 · FHIR同步',
    pagePatientPortal: '患者门户',
    pagePatientPortalSub: '自助服务访问 · 安全消息 · 记录',
    pageProviderManagement: '医疗提供者管理',
    pageProviderManagementSub: '资质认证 · 排班 · 绩效 · 所有提供者',
    pageScheduling: '预约排班',
    pageSchedulingSub: '日历 · 可用性 · 提醒 · 所有地点',
    pageTelemedicine: '远程医疗',
    pageTelemedicineSub: '虚拟就诊 · 视频通话 · 远程护理',
    pageBillingClaims: '账单与索赔',
    pageBillingClaimsSub: '收入周期管理 · AI辅助编码 · 所有地点',
    pagePharmacyPOS: '药房POS',
    pagePharmacyPOSSub: '配药 · 库存 · 药物相互作用 · 所有地点',
    pageAIBots: 'AI机器人',
    pageAIBotsSub: '智能自动化 · 前台 · 临床笔记 · 账单',
    pageTeamMessaging: '团队消息',
    pageTeamMessagingSub: '安全临床通信 · 符合HIPAA',
    pagePatientCommunication: '患者沟通',
    pagePatientCommunicationSub: '短信 · 邮件 · 门户消息 · 广播',
    pageNotifications: '通知',
    pageNotificationsSub: '实时警报 · 临床 · 账单 · 系统',
    pageReports: '报告与商业智能',
    pageReportsSub: '分析 · 导出 · 合规报告 · 所有地点',
    pageAnalyticsDashboard: '实时分析',
    pageAnalyticsDashboardSub: '实时KPI · 收入 · 患者流量 · 提供者绩效',
    pageWorkflowAutomation: '工作流自动化',
    pageWorkflowAutomationSub: '自动化工作流 · 触发器 · 操作 · AI',
    pageAuditLog: '审计日志与合规',
    pageAuditLogSub: 'PHI访问日志 · HIPAA合规 · 安全事件',
    pagePatientDataExport: '患者数据导出',
    pagePatientDataExportSub: 'FHIR R4 · CSV · JSON · 批量导出 · 符合HIPAA',
    pageSettings: '设置',
    pageSettingsSub: '系统配置 · 集成 · 用户偏好',
    save: '保存',
    cancel: '取消',
    delete: '删除',
    edit: '编辑',
    add: '添加',
    view: '查看',
    export: '导出',
    filter: '筛选',
    searchPlaceholder: '搜索...',
    loading: '加载中...',
    noData: '暂无数据',
    refresh: '刷新',
    status: '状态',
    actions: '操作',
    date: '日期',
    time: '时间',
    name: '姓名',
    email: '邮箱',
    phone: '电话',
    active: '活跃',
    inactive: '非活跃',
    pending: '待处理',
    completed: '已完成',
    cancelled: '已取消',
    all: '全部',
    today: '今天',
    thisWeek: '本周',
    thisMonth: '本月',
    custom: '自定义',
    patients: '患者',
    billing: '账单',
    pharmacy: '药房',
    reports: '报告',
    dashboard: '仪表板',
    aiPoweredCare: 'AI驱动的医疗。',
    aiPoweredCareSub: '随处可用。',
    hipaaCompliant: 'HIPAA合规 · SOC 2 II型 · FHIR R4',
  },

  hi: {
    welcomeBack: 'वापस स्वागत है',
    signIn: 'साइन इन करें',
    createAccount: 'खाता बनाएं',
    signInToWorkspace: 'अपने NobiMED AI कार्यक्षेत्र में साइन इन करें',
    forgotPassword: 'पासवर्ड भूल गए?',
    emailAddress: 'ईमेल पता',
    password: 'पासवर्ड',
    rememberMe: '30 दिनों के लिए साइन इन रहें',
    signInBtn: 'NobiMED AI में साइन इन करें',
    orContinueWith: 'या ईमेल से जारी रखें',
    role: 'भूमिका',
    location: 'स्थान',
    search: 'खोजें...',
    notifications: 'सूचनाएं',
    settings: 'सेटिंग्स',
    logout: 'लॉग आउट',
    aiBotActive: 'AI बॉट सक्रिय',
    fhirConnected: 'FHIR जुड़ा हुआ',
    markAllRead: 'सभी पढ़े हुए चिह्नित करें',
    allCaughtUp: 'सब ठीक है!',
    noNewNotifications: 'कोई नई सूचना नहीं',
    darkMode: 'डार्क मोड में बदलें',
    lightMode: 'लाइट मोड में बदलें',
    groupClinical: 'क्लिनिकल',
    groupRevenue: 'राजस्व',
    groupPharmacy: 'फार्मेसी',
    groupAI: 'AI',
    groupCommunication: 'संचार',
    groupAnalytics: 'विश्लेषण',
    groupCompliance: 'अनुपालन',
    navClinicalDashboard: 'क्लिनिकल डैशबोर्ड',
    navPatients: 'मरीज़',
    navPatientPortal: 'मरीज़ पोर्टल',
    navProviders: 'प्रदाता',
    navScheduling: 'शेड्यूलिंग',
    navTelemedicine: 'टेलीमेडिसिन',
    navBillingClaims: 'बिलिंग और दावे',
    navPharmacyPOS: 'फार्मेसी POS',
    navAIBots: 'AI बॉट्स',
    navTeamMessaging: 'टीम संदेश',
    navPatientCommunication: 'मरीज़ संचार',
    navNotifications: 'सूचनाएं',
    navReports: 'रिपोर्ट और BI',
    navAnalyticsDashboard: 'विश्लेषण डैशबोर्ड',
    navWorkflowAutomation: 'वर्कफ़्लो स्वचालन',
    navAuditLog: 'ऑडिट लॉग',
    navPatientDataExport: 'डेटा निर्यात',
    navSettings: 'सेटिंग्स',
    navHelpSupport: 'सहायता और समर्थन',
    navCredentialVerification: 'क्रेडेंशियल वायरीफिएक्शन',
    navGLConsolidated: 'GL समेकित दृश्य',
    navVendorPurchase: 'विक्रेता खरीद',
    navInventoryManagement: 'इन्वेंटरी प्रबंधन',
    navAccounting: 'लेखांकन',
    navReconciliation: 'सुलह',
    pageClinicalAnalytics: 'क्लिनिकल विश्लेषण',
    pageClinicalAnalyticsSub: 'सभी स्थान · लाइव',
    pagePatientManagement: 'मरीज़ प्रबंधन',
    pagePatientManagementSub: 'सक्रिय रजिस्ट्री · 3 स्थान · FHIR सिंक',
    pagePatientPortal: 'मरीज़ पोर्टल',
    pagePatientPortalSub: 'स्व-सेवा पहुंच · सुरक्षित संदेश · रिकॉर्ड',
    pageProviderManagement: 'प्रदाता प्रबंधन',
    pageProviderManagementSub: 'प्रमाणपत्र · शेड्यूल · प्रदर्शन · सभी प्रदाता',
    pageScheduling: 'अपॉइंटमेंट शेड्यूलिंग',
    pageSchedulingSub: 'कैलेंडर · उपलब्धता · अनुस्मारक · सभी स्थान',
    pageTelemedicine: 'टेलीमेडिसिन',
    pageTelemedicineSub: 'वर्चुअल विज़िट · वीडियो कॉल · दूरस्थ देखभाल',
    pageBillingClaims: 'बिलिंग और दावे',
    pageBillingClaimsSub: 'राजस्व चक्र · AI-सहायता कोडिंग · सभी स्थान',
    pagePharmacyPOS: 'फार्मेसी POS',
    pagePharmacyPOSSub: 'वितरण · इन्वेंटरी · इंटरैक्शन · सभी स्थान',
    pageAIBots: 'AI बॉट्स',
    pageAIBotsSub: 'स्मार्ट स्वचालन · फ्रंट डेस्क · क्लिनिकल नोट्स · बिलिंग',
    pageTeamMessaging: 'टीम संदेश',
    pageTeamMessagingSub: 'सुरक्षित क्लिनिकल संचार · HIPAA अनुपालित',
    pagePatientCommunication: 'मरीज़ संचार',
    pagePatientCommunicationSub: 'SMS · ईमेल · पोर्टल संदेश · प्रसारण',
    pageNotifications: 'सूचनाएं',
    pageNotificationsSub: 'रीयल-टाइम अलर्ट · क्लिनिकल · बिलिंग · सिस्टम',
    pageReports: 'रिपोर्ट और बिज़नेस इंटेलिजेंस',
    pageReportsSub: 'विश्लेषण · निर्यात · अनुपालन रिपोर्ट · सभी स्थान',
    pageAnalyticsDashboard: 'Analítica em Tempo Real',
    pageAnalyticsDashboardSub: 'KPIs en direct · Revenus · Flux patients · Performance',
    pageWorkflowAutomation: 'Automação de Fluxos',
    pageWorkflowAutomationSub: 'Fluxos automatizados · Gatilhos · Ações · IA',
    pageAuditLog: 'Registro de Auditoria y Conformidad',
    pageAuditLogSub: 'Registro de acceso PHI · Conformidad HIPAA · Eventos de seguridad',
    pagePatientDataExport: 'Exportación de Datos de Pacientes',
    pagePatientDataExportSub: 'FHIR R4 · CSV · JSON · Exportación en masa · Cumple HIPAA',
    pageSettings: 'Configuración',
    pageSettingsSub: 'Configuración del sistema · Integrações · Preferencias',
    save: 'Salvar',
    cancel: 'Cancelar',
    delete: 'Excluir',
    edit: 'Editar',
    add: 'Adicionar',
    view: 'Ver',
    export: 'Exportar',
    filter: 'Filtrar',
    searchPlaceholder: 'Buscar...',
    loading: 'Cargando...',
    noData: 'Sin datos disponibles',
    refresh: 'Actualizar',
    status: 'Estado',
    actions: 'Acciones',
    date: 'Fecha',
    time: 'Hora',
    name: 'Nombre',
    email: 'Correo',
    phone: 'Teléfono',
    active: 'Activo',
    inactive: 'Inactivo',
    pending: 'Pendiente',
    completed: 'Completado',
    cancelled: 'Cancelado',
    all: 'Todos',
    today: 'Hoy',
    thisWeek: 'Esta Semana',
    thisMonth: 'Este Mes',
    custom: 'Personalizado',
    patients: 'Pacientes',
    billing: 'Facturación',
    pharmacy: 'Farmácia',
    reports: 'Relatórios',
    dashboard: 'Painel',
    aiPoweredCare: 'Cuidados com IA.',
    aiPoweredCareSub: 'Em Qualquer Lugar.',
    hipaaCompliant: 'Conforme HIPAA · SOC 2 Tipo II · FHIR R4',
  },
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  language: LANGUAGES[0],
  setLanguage: () => {},
  t: (key) => key,
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(LANGUAGES[0]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    if (lang.code === 'ar') {
      document.documentElement.setAttribute('dir', 'rtl');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
    }
  }, []);

  const t = useCallback(
    (key: TranslationKey): string => {
      return translations[language.code]?.[key] ?? translations['en'][key] ?? key;
    },
    [language.code]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
